import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { getAlgolia } from '@boss/algolia-client';
import { CHANNEL, stepByStepPageSlugs } from '@boss/constants/b2b-b2c';
import { contentfulContext } from '@boss/contentful-client';
import { content as contentService } from '@boss/services';
import { Environment } from '@boss/types/b2b-b2c';

import { InstantSearch, SEO } from '../../components';
import { IS_BUILD, PageTypes } from '../../constants';
import { RoadmapOverviewPage } from '../../containers';
import Layout from '../../layout/default';
import { getStepByStepSearchIndexName } from '../../utils';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket, mapNextToContentfulMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

export const getStaticProps = async ({ preview, locale }: GetStaticPropsContext) => {
  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }
  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const market = mapNextToContentfulMarket(locale);

  if (!contentfulLocaleMarket || !market) {
    return {
      notFound: true,
    };
  }

  // SET GLOBAL MARKET CONTEXT
  contentfulContext.market = market;

  const [navigation, overviewPage] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    contentService.getOverviewPage({
      locale: contentfulLocaleMarket,
      channel: CHANNEL,
      type: 'Step by step plans',
      batchMode: IS_BUILD,
      preview: !!preview,
      market,
    }),
  ]);

  return {
    props: {
      pageType: PageTypes.ROADMAP_OVERVIEW,
      preview: !!preview,
      pageData: { overviewPage },
      navigation,
      locale,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'stores', 'routes', 'facets', 'forms'])),
    },
  };
};

export const Index = ({
  navigation,
  pageData,
  preview,
  environment,
  locale,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const searchClient = getAlgolia({ isShared: true }).algoliaClient;
  const indexName = getStepByStepSearchIndexName(locale);
  const { t } = useTranslation();
  const { overviewPage } = pageData;
  const pageTitle = t('overviewTitle', { ns: 'routes' }) ?? '';
  const pageDescription = pageTitle; // TODO: Update with final value once this is known
  const crumbles = [{ href: stepByStepPageSlugs[locale], label: pageTitle }];
  const localizedSlugs = stepByStepPageSlugs;

  return (
    <SEO
      breadcrumbs={crumbles}
      description={overviewPage?.metaDescription ?? pageDescription}
      imageSrc={overviewPage?.ogImage?.image?.file?.url}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      noFollow={overviewPage?.noFollow}
      noIndex={overviewPage?.noIndex}
      title={overviewPage?.pageTitle ?? pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <InstantSearch facetFilters={`channel:"${CHANNEL}"`} indexName={indexName} searchClient={searchClient}>
          <RoadmapOverviewPage cmsData={pageData.overviewPage} indexName={indexName} />
        </InstantSearch>
      </Layout>
    </SEO>
  );
};

export default Index;
